"use client";

import Image, { ImageProps } from "next/image";
import { useState } from "react";

/**
 * Card image with a branded fallback. Renders next/image normally, but
 * when the src is missing (empty / undefined / the generic
 * "/property-img.svg" stand-in) OR the image fails to load (S3 404s are
 * common on older listings), it swaps to a calm placeholder: surface
 * background, hairline border and a low-opacity building glyph. The
 * card's own title/location text below stays the primary signal.
 *
 * Client component because next/image onError needs a browser handler.
 * Works in both `fill` and fixed width/height modes — pass the same
 * props you would pass to next/image.
 */

// The legacy "no image" stand-in some callers still pass. Treat it the
// same as a missing image so every card shares one fallback treatment.
const LEGACY_PLACEHOLDER = "/property-img.svg";

interface CardImageProps extends Omit<ImageProps, "src" | "onError"> {
  src?: string | null;
}

function BuildingGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* simple skyline: two buildings + windows */}
      <path
        d="M8 42V16a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v26"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 42V8a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v34"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 42h40"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M13 21h3M13 27h3M13 33h3M27 13h5M27 19h5M27 25h5M27 31h5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function CardImage({
  src,
  alt,
  className,
  fill,
  ...rest
}: CardImageProps) {
  // Track the exact src that failed so a prop change to a new src
  // gets a fresh attempt instead of being stuck on the fallback.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const missing = !src || src === LEGACY_PLACEHOLDER;
  const errored = !missing && failedSrc === src;

  if (missing || errored) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`flex items-center justify-center bg-surface border border-line ${
          fill ? "absolute inset-0 h-full w-full" : ""
        } ${className ?? ""}`}
      >
        <BuildingGlyph className="h-10 w-10 text-ink-faint opacity-50" />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      className={className}
      fill={fill}
      onError={() => setFailedSrc(src)}
      {...rest}
    />
  );
}
